/**
 * サイドパネルの器と内部レジストリ（FR-UI-04・EP-8 の受け皿）。
 * v1 の公開拡張点はツールバーのボタン追加までとし、パネル登録は内部に留める。
 */
import { cls, uniqueId } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { ViewerState } from '../core/viewer';
import type { Translate } from '../i18n/i18n';
import { ICONS } from './icons';

/** パネルの中身。activate は開いたときに呼ばれる（遅延生成の起点）。 */
export interface PanelContent {
  readonly el: HTMLElement;
  activate?(): void;
  /** 状態変化の通知（開いているパネルにだけ届く）。 */
  sync?(state: ViewerState): void;
  dispose?(): void;
}

interface Entry {
  id: string;
  title: string;
  content: PanelContent;
  section: HTMLElement;
}

export class Panels {
  readonly el: HTMLDivElement;

  private readonly entries = new Map<string, Entry>();
  private readonly titleEl: HTMLHeadingElement;
  private readonly body: HTMLDivElement;
  private open: string | null = null;
  /** 開いたときにフォーカスがあった要素（閉じたら戻す — FR-A11Y-01）。 */
  private restoreFocus: HTMLElement | null = null;

  constructor(
    private readonly doc: Document,
    registry: DisposeRegistry,
    private readonly translate: Translate,
  ) {
    const titleId = uniqueId('panel-title');

    this.el = doc.createElement('div');
    this.el.className = cls('panel');
    this.el.hidden = true;
    // 器そのものに名前を与える（M8c-2 手順 4）。中身は開くたび差し替わるので、
    // 名前も見出しに追随する — だから固定文字列ではなく `aria-labelledby` で
    // 見出しを指す（見出しの文言は show() が entry.title で書き換える）。
    this.el.setAttribute('role', 'region');
    this.el.setAttribute('aria-labelledby', titleId);

    const header = doc.createElement('div');
    header.className = cls('panel-header');

    this.titleEl = doc.createElement('h2');
    this.titleEl.className = cls('panel-title');
    this.titleEl.id = titleId;

    const close = doc.createElement('button');
    close.type = 'button';
    close.className = `${cls('button')} ${cls('button--close')}`;
    close.innerHTML = ICONS.close ?? '';
    close.setAttribute('aria-label', translate('panel.close'));
    close.title = translate('panel.close');
    registry.listen(close, 'click', () => this.close());

    header.append(this.titleEl, close);

    this.body = doc.createElement('div');
    this.body.className = cls('panel-body');

    this.el.append(header, this.body);
  }

  register(id: string, title: string, content: PanelContent): void {
    const section = this.doc.createElement('div');
    section.className = cls('panel-section');
    section.hidden = true;
    section.appendChild(content.el);
    this.body.appendChild(section);
    this.entries.set(id, { id, title, content, section });
  }

  toggle(id: string): void {
    if (this.open === id) this.close();
    else this.show(id);
  }

  show(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    // 別のパネルへ切り替えるときは最初の復帰先（開くきっかけになったボタン）を保つ
    if (this.open === null) this.restoreFocus = this.doc.activeElement as HTMLElement | null;
    for (const other of this.entries.values()) other.section.hidden = other.id !== id;
    this.titleEl.textContent = entry.title;
    this.el.hidden = false;
    this.open = id;
    entry.content.activate?.();
  }

  close(): void {
    const wasOpen = this.open !== null;
    this.el.hidden = true;
    this.open = null;
    // 閉じたらフォーカスを開いた場所へ返す。パネルは非モーダルなので閉じ込めはしない。
    if (wasOpen) this.restoreFocus?.focus();
    this.restoreFocus = null;
  }

  get openId(): string | null {
    return this.open;
  }

  /** 開いているパネルにだけ状態を配る（閉じているパネルの再描画は無駄なので送らない）。 */
  sync(state: ViewerState): void {
    if (this.open === null) return;
    this.entries.get(this.open)?.content.sync?.(state);
  }

  dispose(): void {
    for (const entry of this.entries.values()) entry.content.dispose?.();
    this.entries.clear();
    this.el.remove();
  }
}
